import {
  Credenza,
  CredenzaBody,
  CredenzaContent,
  CredenzaDescription,
  CredenzaFooter,
  CredenzaHeader,
  CredenzaTitle,
  CredenzaTrigger,
} from '@/components/ui/credenza.tsx';
import { Button } from '@/components/ui/button.tsx';
import { TrashIcon } from 'lucide-react';
import { ExpandedState, Row, RowData, RowSelectionState } from '@tanstack/react-table';
import React, { useState } from 'react';
import { TTreeDevice } from '@/features/devices/schemas/devices.schemas.ts';
import { useTranslation } from 'react-i18next';

export const DeviceTableToolbarDeleteButton = ({
  data,
  hasSelectedRow,
  getSelectedTableRow,
  getSelectedDevice,
  setData,
  setExpandedRows,
  setSelectedRows,
}: {
  data: TTreeDevice[];
  hasSelectedRow: boolean;
  getSelectedTableRow: () => Row<RowData> | undefined;
  getSelectedDevice: () => TTreeDevice | null;
  setData: (devices: TTreeDevice[]) => void;
  setExpandedRows: React.Dispatch<React.SetStateAction<ExpandedState>>;
  setSelectedRows: React.Dispatch<React.SetStateAction<RowSelectionState>>;
}) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const { t } = useTranslation();

  const handleDeviceDelete = () => {
    const selectedRow = getSelectedTableRow();

    if (!selectedRow) {
      return;
    }

    const deleteFromNode = (devices: TTreeDevice[], targetPath: number[]): TTreeDevice[] => {
      if (targetPath.length === 0) {
        return devices;
      }

      if (targetPath.length === 1) {
        return devices.filter((_, index) => index !== targetPath[0]);
      }

      return devices.map((device, index) => {
        if (index === targetPath[0]) {
          return {
            ...device,
            subDevices: device.subDevices
              ? deleteFromNode(device.subDevices, targetPath.slice(1))
              : device.subDevices,
          };
        }
        return device;
      });
    };

    const pathIndices = selectedRow.id.split('.').map(Number);

    const newData = deleteFromNode(data, pathIndices);
    setData(newData);

    setSelectedRows({});

    setExpandedRows((prev) => {
      const newExpanded: Record<string, boolean> = typeof prev === 'object' ? { ...prev } : {};
      Object.keys(newExpanded).forEach((key) => {
        if (key.startsWith(selectedRow.id)) {
          delete newExpanded[key];
        }
      });
      return newExpanded;
    });

    setIsDialogOpen(false);
  };

  const selectedDevice = getSelectedDevice();
  const hasSubDevices = selectedDevice?.subDevices && selectedDevice.subDevices.length > 0;

  return (
    <Credenza open={isDialogOpen} onOpenChange={setIsDialogOpen}>
      <CredenzaTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          className="flex items-center gap-2 rounded-none py-5 font-normal hover:bg-gray-200"
          disabled={!hasSelectedRow}
        >
          <TrashIcon className="text-muted-foreground size-4" />
          <span>{t('common.buttons.deleteButton')}</span>
        </Button>
      </CredenzaTrigger>
      <CredenzaContent>
        <CredenzaHeader>
          <CredenzaTitle>{t('devices.actions.confirmDelete')}</CredenzaTitle>
        </CredenzaHeader>
        <CredenzaBody>
          <CredenzaDescription className="hidden">
            {t('devices.actions.confirmDelete')}
            {hasSubDevices ? ' and all its connected sub-devices' : ''} t
            {'devices.actions.system'}
          </CredenzaDescription>
          {hasSubDevices ? (
            <>
              {t('devices.actions.questionDeletion1')} {selectedDevice?.SN}{' '}
              {t('devices.actions.questionDeletion2')}
            </>
          ) : (
            <>
              {t('devices.actions.questionDeletion1')} {selectedDevice?.SN}?
            </>
          )}
        </CredenzaBody>
        <CredenzaFooter>
          <Button type="button" variant="ghost" onClick={() => setIsDialogOpen(false)}>
            {t('common.buttons.cancelButton')}
          </Button>
          <Button type="button" variant="destructive" onClick={handleDeviceDelete}>
            {t('common.buttons.deleteButton')}
          </Button>
        </CredenzaFooter>
      </CredenzaContent>
    </Credenza>
  );
};
